import { AppBar, makeStyles, Tab, Tabs } from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { assignIn, filter, forEach, get, isEmpty, omit } from 'lodash';
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import {
  APPLICANT_LABEL_NAME,
  CYBER_DEFAULT_PLAN,
  CYBER_SECURITY,
  HOME_WARRANTY,
  INSURANCE_BUSINESS_TYPES,
  PAGE_TRACKING_KEYS,
  PRODUCT_MAPPING,
  PROPERTY_INFO,
  QUOTE_APPLICANTION_VIEW_ROUTE,
  QUOTE_APPLICATION_ROUTE,
  RATES_LABEL_NAME,
} from '../constants';
import history from '../history';
import FeedbackButton from '../pages/components/feedback-button';
import TabPanel from '../pages/components/questionnaire-tab-wrapper';
import {
  addDetails,
  addDriver,
  addVehicle,
  cleanHelperProduct,
  clearAutoQuoteSate,
  clearCyberSecurityState,
  clearDriverInfo,
  clearFloodState,
  clearHomeState,
  clearHomeWarrantyState,
  clearInsurenaceDetails,
  clearLifeState,
  clearPetState,
  clearQuakeState,
  clearUmbrellaState,
  clearVehiclesList,
  driverSelectedForEdit,
  fetchPconfig,
  fetchViewQuotes,
  flushAppDetails,
  getQuoteLobDetails,
  setAnnexPrefill,
  setAppointments,
  setAutoQuoteDetails,
  setCyberSecurityQuoteDetails,
  setFloodHerdId,
  setFloodQuotes,
  setHomeState,
  setHomeWarrantyQuoteDetails,
  setLobPriority,
  setMissingSpouse,
  setPetQuotes,
  setQuakeHerdId,
  setQuakeQuotes,
  setQuoteCompleted,
  setRatePageEnable,
  setRedirectToViewQuoteFlag,
  setRequestId,
  setSwitchDetailFromDriver,
  setVehicleDetails,
  setViewQuote,
  storeBoundedCarrierDetails,
  storeLifeQuoteDetails,
  storePetListData,
  storeUmbrellaQuoteDetails,
  updateQuoteDetails,
  updateSelectedCarriers,
  vehicleSelect,
  vinSubmitSuccess,
} from '../pages/redux/actions';
import { useAppDispatch, useAppSelector } from '../pages/redux/hooks';
import { ReduxRootState } from '../pages/redux/store';
import {
  checkSpouseMissing,
  getHerdId,
  getHerdIdFromUrl,
  getLobHerdId,
  getQuoteListOfLob,
  getSeperateQuoteList,
  getStepperTabLength,
  goToDashboard,
  inIframe,
  isApplicantDataRequired,
  isAutoQuote,
  loadQuotesOnRatePage,
  updateApplicantinfo,
} from '../utils';
import {
  createUmbrellaQuestionsMapping,
  getMappedUmbrellaQuestionnaireGroups,
} from '../utils/umbrella';
import { LineOfBusiness } from './enums';
import './form.scss';
import './stepper-list.scss';
import { allProps, tabElements } from './tabs';
import CustomIcon from './tabs/icon';
import {
  createFloodQuestionnaire,
  showPropertyInfoForFloodLob,
  showPropertyInfoForHomeLob,
} from './utils';
import { FoundationType } from '../graphql/generated';

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
  },
});

const AUTO_TABS_COUNT = 3;
const OTHER_TABS_COUNT = 2;
const BASE_DRIVER = 2;
const BASE_COVERAGES = 3;
const excludeLOBFromStepper = ['HOME', 'QUAKE', 'HOMEWARRANTY', 'CYBERSECURITY'];

const ApplicantForm = lazy(() => import('./pages/applicant'));
const PropertyInfo = lazy(() => import('./pages/property-info'));
const ProductRatesDetails = lazy(() => import('./pages/rates'));

const QuoteApplication = () => {
  const {
    home,
    partner: { appointments, storedRequestId, loader },
    floodDetails,
    quote,
    common: {
      pconfig,
      newProducts,
      redirectToViewQuote,
      isVeriskLoading,
      enableRatePage,
      policyBounded,
      pageTracking,
    },
    vehicle: { vinLoader, vehiclesList },
    petDetails,
    viewQuoteDetails,
    quakeDetails,
    driver: { driversList },
    insurance,
    applicant,
    lifeDetails,
    umbrellaDetails,
    propertyInfoDetails: { propertyInfo }
  }: ReduxRootState = useAppSelector((state: ReduxRootState) => state);
  const dispatch = useAppDispatch();
  const [value, setValue] = useState(0);
  const [tabsCount, setTabsCount] = useState(0);
  const [tabKey, setTabKey] = useState<Array<string>>([]);
  const [goToRates, setGoToRates] = useState<boolean>(false);
  const [lobTab, setLobTab] = useState<{ [key: string]: number }>({});
  const [add, setAdd] = useState(false);
  const [requestIdInState, setRequestIdInState] = useState<any>(storedRequestId);
  const [veriskLoading, setVeriskLoading] = useState(false);
  const [quoteType, setQuoteType] = useState('');
  let ref = useRef<any>();
  const classes = useStyles();
  const enableFeedbackButton =
    !!home?.pconfig?.dale_config?.feedback_enabled ||
    !!home?.pconfig?.pconfig?.dale_config?.feedback_enabled;
  const appointmentList: Array<any> = !isEmpty(appointments) ? Object.keys(appointments) : [];
  const stepperList: Array<any> = appointmentList.concat(Object.keys(newProducts));
  const isHome: boolean = stepperList.includes(INSURANCE_BUSINESS_TYPES.HOME);
  const isFlood: boolean = stepperList.includes(INSURANCE_BUSINESS_TYPES.FLOOD);
  const isShowPropertyInfoForHome: boolean = isHome && showPropertyInfoForHomeLob();
  const isShowPropertyInfoForFlood: boolean = isFlood && showPropertyInfoForFloodLob();
  const isShowPropertyInfo: boolean = isShowPropertyInfoForHome || isShowPropertyInfoForFlood;
  const homeHerdId = getLobHerdId(LineOfBusiness.HOME) ?? '';
  const floodHerdId = getLobHerdId(LineOfBusiness.FLOOD) ?? '';
  const homeQuoteList = getQuoteListOfLob(LineOfBusiness.HOME);
  const floodQuoteList = getQuoteListOfLob(LineOfBusiness.FLOOD);
  const isCompletedHomeOrFlood = !isEmpty(homeQuoteList) || !isEmpty(floodQuoteList);
  const checkForPropertyInfoDisabled = isHome && isFlood && isCompletedHomeOrFlood ? false : true;
  const isDisabledPropertyInfo: boolean =
    checkForPropertyInfoDisabled &&
    ((isHome && isEmpty(homeHerdId)) || (isFlood && isEmpty(floodHerdId)));
  

  const setQuestionnaireData = () => {
    const appointmentData: any = {
      HOME: {},
      QUAKE: {},
      HOMEWARRANTY: {},
      CYBERSECURITY: {},
      FLOOD: {},
      AUTO: {},
      PET: {},
      LIFE: {},
      UMBRELLA: {},
    };
    dispatch(setAppointments({ appointments: appointmentData }));
    const { quoteLobDetails } = quote;
    if (quoteLobDetails?.quotesByLOB && !isEmpty(Object.keys(quoteLobDetails?.quotesByLOB))) {
      Object.keys(appointmentData).forEach(key => {
        let lobkey = PRODUCT_MAPPING[key] || key;
        if (!quoteLobDetails.quotesByLOB[lobkey.toLowerCase()]) {
          delete appointmentData[key];
        }
      });
    }

    if (quoteLobDetails?.carriersBounded) {
      const { carriersBounded } = quoteLobDetails;
      if (!!carriersBounded?.earthquake) {
        carriersBounded.quake = carriersBounded?.earthquake;
        delete carriersBounded.earthquake;
      }
      dispatch(storeBoundedCarrierDetails(carriersBounded));
    } else {
      dispatch(storeBoundedCarrierDetails({}));
    }

    dispatch(clearHomeState());
    if (quoteLobDetails?.quotesByLOB?.flood) {
      dispatch(setFloodHerdId(quoteLobDetails?.quotesByLOB?.flood?.herdId));
      if (quoteLobDetails.quotesByLOB.flood.quotes) {
        const { errorQuotes } = getSeperateQuoteList(
          quoteLobDetails?.quotesByLOB?.flood?.error_details,
          {}
        );
        dispatch(
          setFloodQuotes({
            quoteList: quoteLobDetails?.quotesByLOB?.flood?.quotes
              ? quoteLobDetails.quotesByLOB.flood.quotes
              : [],
            quoteErrorList: errorQuotes,
            selectedCarrier: quoteLobDetails?.selectedCarriers?.flood?.carrierId
              ? quoteLobDetails.selectedCarriers.flood
              : {},
          })
        );
      }
    } else {
      dispatch(clearFloodState());
    }

    if (quoteLobDetails?.quotesByLOB?.quake) {
      dispatch(setQuakeHerdId(quoteLobDetails?.quotesByLOB?.quake?.herdId));
      const { errorQuotes } = getSeperateQuoteList(
        quoteLobDetails?.quotesByLOB?.quake?.error_details,
        {}
      );
      dispatch(
        setQuakeQuotes({
          quoteList: [
            ...Object.values(quoteLobDetails?.quotesByLOB?.quake?.quotes || []),
            ...Object.values(errorQuotes),
          ],
          selectedCarrier: quoteLobDetails?.selectedCarriers?.quake?.carrierId
            ? quoteLobDetails.selectedCarriers.quake
            : {},
        })
      );
    } else {
      dispatch(clearQuakeState());
    }

    if (quoteLobDetails?.quotesByLOB?.pet) {
      let errorQuotes: any = [];
      if (quoteLobDetails?.quotesByLOB?.pet?.error_details) {
        errorQuotes = filter(
          Object.values(quoteLobDetails?.quotesByLOB?.pet?.error_details?.errors),
          error => {
            return assignIn(error, { quoteError: true });
          }
        );
      }
      dispatch(
        setPetQuotes({
          quoteList: [...Object.values(quoteLobDetails?.quotesByLOB?.pet?.quotes), ...errorQuotes],
          herdId: quoteLobDetails?.quotesByLOB?.pet?.herdId,
        })
      );
      dispatch(storePetListData(quoteLobDetails?.quotesByLOB?.pet?.pet_details));
    } else {
      dispatch(clearPetState());
    }

    if (quoteLobDetails?.quotesByLOB[HOME_WARRANTY]) {
      let errorQuotes: any = [];
      let homeWarrantyQuoteDetails = quoteLobDetails?.quotesByLOB[HOME_WARRANTY];
      if (homeWarrantyQuoteDetails?.error_details) {
        errorQuotes = filter(
          Object.values(homeWarrantyQuoteDetails?.error_details?.errors),
          error => {
            return assignIn(error, { quoteError: true });
          }
        );
      }
      dispatch(
        setHomeWarrantyQuoteDetails({
          quoteList: [...Object.values(homeWarrantyQuoteDetails?.quotes), ...errorQuotes],
          herdId: homeWarrantyQuoteDetails?.herdId,
        })
      );
    } else {
      dispatch(clearHomeWarrantyState());
    }

    if (quoteLobDetails?.quotesByLOB[CYBER_SECURITY]) {
      let errorQuotes: any = [];
      let cyberSecurityQuoteDetails = quoteLobDetails?.quotesByLOB[CYBER_SECURITY];
      if (cyberSecurityQuoteDetails?.error_details?.errors) {
        errorQuotes = filter(
          Object.values(cyberSecurityQuoteDetails?.error_details?.errors),
          error => {
            return assignIn(error, { quoteError: true });
          }
        );
      }
      if (cyberSecurityQuoteDetails?.quotes?.length) {
        let selectedCarrier =
          quoteLobDetails?.selectedCarriers && quoteLobDetails?.selectedCarriers[CYBER_SECURITY];
        filter(cyberSecurityQuoteDetails.quotes, quote => {
          let selectedPlan =
            (selectedCarrier &&
              selectedCarrier?.carrierId?.toLowerCase() === quote?.carrier_id?.toLowerCase() &&
              selectedCarrier?.selectedPlan) ||
            CYBER_DEFAULT_PLAN;
          return assignIn(quote, {
            premiumDetails: quote[selectedPlan],
            selectedPlan: selectedPlan,
          });
        });
      }
      dispatch(
        setCyberSecurityQuoteDetails({
          quoteList: !isEmpty(cyberSecurityQuoteDetails?.quotes)
            ? cyberSecurityQuoteDetails?.quotes
            : [],
          quoteErrorList: !isEmpty(errorQuotes) ? errorQuotes : [],
          quoteCompleted: true,
          herdId: cyberSecurityQuoteDetails?.herdId,
        })
      );
    } else {
      dispatch(
        setCyberSecurityQuoteDetails({
          quoteList: [],
          quoteErrorList: [],
          upcomingCarrierList: [],
        })
      );
      dispatch(clearCyberSecurityState());
    }

    if (quoteLobDetails?.quotesByLOB?.life) {
      let lifeQuoteDetails = quoteLobDetails?.quotesByLOB?.life;
      let errorQuotes: any = [];
      if (lifeQuoteDetails?.error_details?.errors) {
        errorQuotes = filter(Object.values(lifeQuoteDetails?.error_details?.errors), error => {
          return assignIn(error, { quoteError: true });
        });
      }
      dispatch(
        storeLifeQuoteDetails({
          quoteList: Object.values(lifeQuoteDetails?.quotes),
          quoteErrorList: errorQuotes,
          quoteCompleted: true,
          herdId: lifeQuoteDetails?.herdId,
          lifeInfo: quoteLobDetails?.quotesByLOB?.life?.lifeInfo,
        })
      );
    } else {
      dispatch(
        storeLifeQuoteDetails({
          quoteList: [],
          quoteErrorList: [],
          upcomingCarrierList: [],
        })
      );
      dispatch(clearLifeState());
    }

    if (quoteLobDetails?.quotesByLOB?.umbrella) {
      let umbrellaQuoteDetails = quoteLobDetails?.quotesByLOB?.umbrella;
      let errorQuotes: any = [];
      if (umbrellaQuoteDetails?.error_details?.errors) {
        errorQuotes = filter(Object.values(umbrellaQuoteDetails?.error_details?.errors), error => {
          return assignIn(error, { quoteError: true });
        });
      }
      dispatch(
        storeUmbrellaQuoteDetails({
          quoteList: Object.values(umbrellaQuoteDetails?.quotes),
          quoteErrorList: errorQuotes,
          quoteCompleted: true,
          herdId: umbrellaQuoteDetails?.herdId,
          umbrellaInfo: {
            questionGroups:
              umbrellaQuoteDetails?.rliQuestionInfo &&
              !isEmpty(umbrellaQuoteDetails?.rliQuestionInfo)
                ? getMappedUmbrellaQuestionnaireGroups(
                    createUmbrellaQuestionsMapping([...umbrellaQuoteDetails?.rliQuestionInfo])
                  )
                : [],
          },
        })
      );
    } else {
      dispatch(
        storeUmbrellaQuoteDetails({
          quoteList: [],
          quoteErrorList: [],
          upcomingCarrierList: [],
        })
      );
      dispatch(clearUmbrellaState());
    }

    if (quoteLobDetails?.quotesByLOB?.auto) {
      if (!isEmpty(quoteLobDetails?.quotesByLOB?.auto?.vehicle_details)) {
        quoteLobDetails.quotesByLOB.auto.vehicle_details.forEach((vehicle: any, index: number) => {
          setTimeout(() => {
            const vehicleData = {
              ...vehicle,
              bodyTypeList: vehicle.body_type
                ? [{ label: vehicle.body_type, value: vehicle.body_type }]
                : [],
              clearedVehicle: {},
              vinInput: true,
              vinLoader: false,
              vinServerError: false,
              vehicleSelectorDetails: {
                make: vehicle.make,
                model: vehicle.model,
                year: vehicle.year,
                makeList: [],
                modelList: [],
                yearList: [],
                vehicleSelectorLoader: false,
              },
              defaultVin: vehicle.vin,
              collision: vehicle.coverage.collision,
              towing_labor: vehicle.coverage.towing_labor,
              comprehensive: vehicle.coverage.comprehensive,
              ext_trans_expense: vehicle.coverage.ext_trans_expense,
              clear: true,
              make: vehicle.make,
              model: vehicle.model,
              year: vehicle.year,
              isIncluded: get(vehicle, 'isIncluded', true),
            };
            dispatch(vinSubmitSuccess(vehicleData));
            dispatch(addVehicle(vehicleData));
            if (index === quoteLobDetails.quotesByLOB.auto.vehicle_details.length - 1) {
              dispatch(vehicleSelect(vehicle.vuid));
            }
          }, 100);
        });
      } else {
        dispatch(clearVehiclesList());
      }

      if (!isEmpty(quoteLobDetails?.quotesByLOB?.auto?.driver_details)) {
        quoteLobDetails?.quotesByLOB?.auto?.driver_details?.forEach((driver: any) => {
          const driverData = {
            ...driver,
            isIncluded: get(driver, 'isIncluded', true),
          };
          dispatch(addDriver(omit(driverData, ['isFetchAutoQuotes'])));
        });
      } else {
        dispatch(clearDriverInfo());
      }

      if (quoteLobDetails?.quotesByLOB?.auto?.insurance_other_details) {
        Object.assign(
          quoteLobDetails.quotesByLOB.auto.insurance_other_details,
          quoteLobDetails?.quotesByLOB?.auto?.insurance_other_details?.coverage
        );
        dispatch(addDetails(quoteLobDetails.quotesByLOB.auto.insurance_other_details));
      } else {
        dispatch(clearInsurenaceDetails());
      }

      dispatch(updateQuoteDetails({ ...quoteLobDetails.quotesByLOB.auto }));
      dispatch(
        setAutoQuoteDetails({
          quotes: quoteLobDetails?.quotesByLOB?.auto?.quotes
            ? Object.values(quoteLobDetails.quotesByLOB.auto.quotes)
            : [],
          errors: quoteLobDetails.quotesByLOB?.auto?.error_details?.errors
            ? Object.values(quoteLobDetails.quotesByLOB.auto.error_details.errors)
            : [],
        })
      );
      if (quoteLobDetails.quotesByLOB.auto.quotes) {
        dispatch(setQuoteCompleted({ quoteCompleted: true } as any));
      }
    } else {
      dispatch(
        setAutoQuoteDetails({
          quotes: [],
          errors: [],
        })
      );
      dispatch(clearDriverInfo());
      dispatch(clearAutoQuoteSate());
    }

    if (quoteLobDetails) {
      updateApplicantinfo(quoteLobDetails?.applicant, quoteLobDetails?.address);
    }

    if (quoteLobDetails?.selectedCarriers) {
      dispatch(updateSelectedCarriers(quoteLobDetails?.selectedCarriers));
    }
    createFloodQuestionnaire(
      (propertyInfo?.building?.foundation?.type as FoundationType) || FoundationType.ConcreteSlab
    );
    if (quoteLobDetails?.annexRequiredPrefill) {
      dispatch(setAnnexPrefill(quoteLobDetails?.annexRequiredPrefill || {}));
    }
  };

  useEffect(() => {
    if (home.pconfig?.dale_config?.is_verisk_prefill_available) {
      if (
        newProducts &&
        Object.keys(newProducts).length &&
        isAutoQuote(Object.keys(newProducts)) &&
        tabsCount === value
      ) {
        const loading = vinLoader || isVeriskLoading;

        setVeriskLoading(loading);
      }
    }
    // eslint-disable-next-line
  }, [vinLoader, isVeriskLoading]);

  useEffect(() => {
    // Clearing appointments to show rate page in dashboard row click
    const { isHerdIdPresent } = getHerdIdFromUrl();
    if (isHerdIdPresent && !quote?.quoteLobDetails && Object.keys(appointments).length) {
      dispatch(setAppointments({ appointments: {} }));
    }
    // eslint-disable-next-line
  }, [appointments]);

  useEffect(() => {
    if (
      tabKey.length &&
      value === tabsCount &&
      Object.keys(appointments).length &&
      !enableRatePage
    ) {
      dispatch(setRatePageEnable(true));
      dispatch(setViewQuote({ isRedirectToApplicant: false }));
    }
    // eslint-disable-next-line
  }, [
    floodDetails.submitLoader,
    quote.quotesLoading,
    quakeDetails.quakeSubmitLoader,
    petDetails,
    policyBounded,
  ]);

  useEffect(() => {
    const keys = Object.keys(appointments);
    if (
      home.pconfig?.dale_config?.is_verisk_prefill_available &&
      newProducts &&
      Object.keys(newProducts).length &&
      isAutoQuote(Object.keys(newProducts))
    ) {
      setVeriskLoading(state => {
        if (!state) {
          setTabKey(keys);
        }
        return state;
      });
    } else {
      setTabKey(keys);
    }
    // eslint-disable-next-line
  }, [appointments, newProducts, veriskLoading]);

  const getStepperIndex = (type: any, tabsElements: any) => {
    let indexValue = 1;
    let isAutoLob = tabsElements.includes(INSURANCE_BUSINESS_TYPES.AUTO);
    if (
      (type === INSURANCE_BUSINESS_TYPES.PET ||
        type === INSURANCE_BUSINESS_TYPES.LIFE ||
        type === INSURANCE_BUSINESS_TYPES.UMBRELLA) &&
      isAutoLob
    ) {
      indexValue = 3;
    }
    return indexValue;
  };

  const createStepper = (tabKeys: any) => {
    let count = 0;
    forEach(
      tabKeys.filter((lob: any) => !excludeLOBFromStepper.includes(lob)),
      (element, index) => {
        let stepperIndex = getStepperIndex(element, tabKeys);
        setLobTab((prevState: any) => ({ ...prevState, [element]: index + stepperIndex }));
        if (element === INSURANCE_BUSINESS_TYPES.AUTO) {
          setLobTab((prevState: any) => ({
            ...prevState,
            DRIVER: index + BASE_DRIVER,
            COVERAGES: index + BASE_COVERAGES,
          }));
          count += AUTO_TABS_COUNT;
        } else {
          count++;
        }
      }
    );
    if (count > 0) {
      count += OTHER_TABS_COUNT;
    } else {
      count = OTHER_TABS_COUNT;
      forEach(tabKeys, (element, index) => {
        setLobTab((prevState: any) => ({ ...prevState, [element]: index + 1 }));
      });
    }
    return count;
  };

  const getStepRedirectValue = (appointments: any) => {
    let redirectionTab = 0;
    if (appointments.includes(INSURANCE_BUSINESS_TYPES.FLOOD)) {
      redirectionTab = lobTab[INSURANCE_BUSINESS_TYPES.FLOOD];
    } else if (appointments.includes(INSURANCE_BUSINESS_TYPES.AUTO)) {
      redirectionTab = isApplicantDataRequired() ? 0 : lobTab[INSURANCE_BUSINESS_TYPES.AUTO];
    } else if (appointments.includes(INSURANCE_BUSINESS_TYPES.PET)) {
      redirectionTab = isApplicantDataRequired() ? 0 : lobTab[INSURANCE_BUSINESS_TYPES.PET];
    } else if (appointments.includes(INSURANCE_BUSINESS_TYPES.LIFE)) {
      redirectionTab = lobTab[INSURANCE_BUSINESS_TYPES.LIFE];
    } else if (appointments.includes(INSURANCE_BUSINESS_TYPES.UMBRELLA)) {
      redirectionTab = lobTab[INSURANCE_BUSINESS_TYPES.UMBRELLA];
    }
    return redirectionTab;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    let { isHerdIdPresent, herdId } = getHerdIdFromUrl();
    herdId = herdId ? herdId : getHerdId();
    if (
      !isHerdIdPresent &&
      (document.location.href.includes(QUOTE_APPLICANTION_VIEW_ROUTE) ||
        document.location.href.includes('/quote?id'))
    ) {
      dispatch(
        setHomeState({
          showError: true,
          notFoundError: true,
          expiredError: false,
        } as any)
      );
      history.push('/error');
    } else if (
      (document.location.href.includes('/quote?id') &&
        isHerdIdPresent &&
        isEmpty(quote?.quoteLobDetails) &&
        !Object.keys(appointments).length) ||
      (redirectToViewQuote && document.location.href.includes('/quote'))
    ) {
      if (newProducts && Object.keys(newProducts).length) {
        dispatch(cleanHelperProduct());
      }
      if (!viewQuoteDetails?.viewQuoteLoader && !viewQuoteDetails?.validateQuoteUrlLoader) {
        dispatch(fetchViewQuotes({ herdId: herdId } as any));
        dispatch(setRedirectToViewQuoteFlag(false));
      }
    } else if (
      (document.location.href.includes(QUOTE_APPLICANTION_VIEW_ROUTE) &&
        isHerdIdPresent &&
        isEmpty(quote?.quoteLobDetails) &&
        !Object.keys(appointments).length) ||
      (redirectToViewQuote && document.location.href.includes(QUOTE_APPLICATION_ROUTE))
    ) {
      if (newProducts && Object.keys(newProducts).length) {
        dispatch(cleanHelperProduct());
      }
      if (!quote?.viewQuoteLoader && !loader) {
        dispatch(getQuoteLobDetails({ herdId: herdId } as any));
        dispatch(setRedirectToViewQuoteFlag(false));
      }
    } else if (herdId && !Object.keys(appointments).length) {
      setQuestionnaireData();
    } else if (Object.keys(appointments).length) {
      if (tabKey.length) {
        let isPrefillCall = isAutoQuote(Object.keys(newProducts));
        let enablePrefillCall =
          isPrefillCall &&
          (Object.keys(appointments).includes('HOMEWARRANTY') ||
            Object.keys(appointments).includes('CYBERSECURITY') ||
            Object.keys(appointments).includes('LIFE') ||
            Object.keys(appointments).includes('UMBRELLA')) &&
          isApplicantDataRequired();
        let stepperKeys =
          !enablePrefillCall &&
          newProducts &&
          home?.pconfig?.dale_config?.is_verisk_prefill_available &&
          Object.keys(newProducts).length &&
          isPrefillCall
            ? tabKey
            : Object.keys(appointments);
        const count = createStepper(stepperKeys);
        setTabsCount(count);
        if (newProducts && Object.keys(newProducts).length) {
          let newAppointmentKey = Object.keys(newProducts);
          const isApplicantMissing = isEmpty(home?.productType) || isEmpty(floodDetails?.herdId);
          if (
            newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.HOME) &&
            (isApplicantMissing ||
              isShowPropertyInfoForHome ||
              applicant.isAddProductSaveApplicantLoading)
          ) {
            if (!applicant.isAddProductSaveApplicantLoading) {
              if (isApplicantMissing || isEmpty(homeHerdId)) {
                setValue(0);
              } else {
                redirectToStep(PROPERTY_INFO.KEY);
              }
            }
          } else if (
            ((newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.FLOOD) ||
              newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.QUAKE)) &&
              isApplicantMissing) ||
            ((isEmpty(home?.personal_details?.education) ||
              isEmpty(home?.personal_details?.industry) ||
              isEmpty(home?.personal_details?.occupation)) &&
              !newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.HOMEWARRANTY) &&
              !(
                (newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.CYBERSECURITY) ||
                  newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.LIFE) ||
                  newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.UMBRELLA)) &&
                !isEmpty(home?.personal_details?.date_of_birth)
              ))
          ) {
            setValue(0);
          } else {
            if (
              loadQuotesOnRatePage(floodDetails?.annexPrefill, newProducts) ||
              (newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.QUAKE) &&
                !newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.HOME) &&
                isEmpty(floodDetails?.herdId))
            ) {
              setValue(count);
            } else if (newAppointmentKey.includes(INSURANCE_BUSINESS_TYPES.HOME)) {
              setValue(0);
            } else {
              if (home.pconfig?.dale_config?.is_verisk_prefill_available) {
                if (isAutoQuote(Object.keys(newProducts)) && !enablePrefillCall) {
                  let redirectToAuto = tabKey.includes('FLOOD') ? 2 : 1;
                  setVeriskLoading(state => {
                    if (!state) {
                      setValue(redirectToAuto);
                    }
                    return state;
                  });
                } else {
                  let redirectionTab = getStepRedirectValue(newAppointmentKey);
                  setValue(redirectionTab);
                }
              } else {
                let redirectionTab = getStepRedirectValue(newAppointmentKey);
                setValue(redirectionTab);
              }
            }
          }
        }

        if (!(newProducts && Object.keys(newProducts).length) && goToRates) {
          setQuoteType('');
          setGoToRates(false);
          setTabsCount(state => {
            setValue(() => state);
            return state;
          });
        }

        if (!(newProducts && Object.keys(newProducts).length) && value > 0 && tabsCount === value) {
          setValue(count);
        }
      }

      // Maintain same request id for a quote
      if (!requestIdInState) {
        let uniqueID = v4();
        uniqueID = !isEmpty(quote?.quoteLobDetails?.providerRequestId)
          ? quote?.quoteLobDetails?.providerRequestId
          : uniqueID;
        setRequestIdInState(uniqueID);
        ref.current = uniqueID;
        dispatch(setRequestId(uniqueID));
      }
    }
    // eslint-disable-next-line
  }, [
    appointments,
    quote.quoteLobDetails,
    tabKey,
    viewQuoteDetails.validateQuoteUrlLoader,
    loader,
    applicant.isAddProductSaveApplicantLoading,
  ]);

  const calculateActiveTab = useCallback(() => {
    let selectedLOBListArray: any = quote?.quoteLobDetails?.selectedLOBs;
    const appointmentData: any = {
      HOME: {},
      QUAKE: {},
      HOMEWARRANTY: {},
      CYBERSECURITY: {},
      FLOOD: {},
      AUTO: {},
      PET: {},
      LIFE: {},
      UMBRELLA: {},
    };
    if (!isEmpty(selectedLOBListArray)) {
      Object.keys(appointmentData).forEach((key: any) => {
        if (!selectedLOBListArray.includes(key.toUpperCase())) {
          delete appointmentData[key];
        }
      });
      dispatch(setAppointments({ appointments: appointmentData }));
      const count = createStepper(selectedLOBListArray);
      setTabKey(selectedLOBListArray);
      setTabsCount(count);
      if (
        viewQuoteDetails &&
        (viewQuoteDetails?.isRedirectToApplicant || quote?.quoteLobDetails?.isValidationErrorExists)
      ) {
        setValue(0);
      } else {
        setValue(count);
      }
    }
    // eslint-disable-next-line
  }, [quote?.quoteLobDetails]);

  useEffect(() => {
    calculateActiveTab();
    if (
      quote?.quoteLobDetails &&
      home?.pconfig?.dale_config?.code &&
      quote?.quoteLobDetails?.organization?.code &&
      !pconfig
    ) {
      dispatch(fetchPconfig({ code: home?.pconfig?.dale_config?.code }));
    }
    // eslint-disable-next-line
  }, [calculateActiveTab, fetchPconfig]);

  const handleNext = (curTabValue: number) => {
    // todo: define tabs count properly
    if (curTabValue !== tabsCount) {
      setValue(curTabValue + 1);
      setAdd(false);
    }
  };

  const redirectToStep = (stepValue: string) => {
    let lobStepMapping: any = {
      floodInfo: INSURANCE_BUSINESS_TYPES.FLOOD,
      petInfo: INSURANCE_BUSINESS_TYPES.PET,
      vehicleInfo: INSURANCE_BUSINESS_TYPES.AUTO,
      lifeInfo: INSURANCE_BUSINESS_TYPES.LIFE,
      umbrellaInfo: INSURANCE_BUSINESS_TYPES.UMBRELLA,
      propertyInfo: PROPERTY_INFO.KEY,
    };
    if (lobStepMapping[stepValue] === PROPERTY_INFO.KEY) {
      setValue(tabsCount + 1);
    } else if (!isEmpty(lobStepMapping[stepValue])) {
      setValue(lobTab[lobStepMapping[stepValue]]);
      setAdd(false);
    } else {
      setValue(tabsCount);
      setAdd(false);
    }
  };

  const goToLastTab = (curTabValue: number) => {
    if (curTabValue < tabsCount) {
      setValue(tabsCount);
      setQuoteType('');
      setAdd(false);
    }
  };

  const setTab = (lobTabValue: string, addInfo: boolean = false) => {
    if (addInfo) {
      setAdd(addInfo);
    }
    if (lobTab[lobTabValue] < tabsCount) {
      setValue(lobTab[lobTabValue]);
    }
  };

  const handleBack = (curTabValue: number) => {
    let firstTab = 0;
    if (goToRates) {
      firstTab = 1;
    }
    if (curTabValue > firstTab) {
      setValue(curTabValue - 1);
      setAdd(false);
    } else {
      setValue(curTabValue);
    }
  };

  const goToFirstTab = (index: number) => {
    setValue(index);
  };

  const goBackToCertainTab = (targetTab: number, type: any) => {
    const isCybersecurityWithDOB =
      INSURANCE_BUSINESS_TYPES.CYBERSECURITY === type &&
      !isEmpty(home?.personal_details?.date_of_birth);
    const isQuakeLob = INSURANCE_BUSINESS_TYPES.QUAKE === type;
    const isEmptyFloodHerdId = isEmpty(floodHerdId);
    const isHomeLobWithHerdId = INSURANCE_BUSINESS_TYPES.HOME === type && !isEmpty(homeHerdId);
    const isHomeOrQuakeWithProductType =
      (isHomeLobWithHerdId || isQuakeLob) && !isEmpty(home?.productType);

    targetTab = isCybersecurityWithDOB || isHomeOrQuakeWithProductType ? tabsCount : targetTab;

    if (targetTab < tabsCount) {
      let appointmentKeys = Object.keys(appointments);
      let checkLobExist =
        (appointmentKeys.includes('HOME') ||
          appointmentKeys.includes('QUAKE') ||
          appointmentKeys.includes('PET') ||
          appointmentKeys.includes('AUTO') ||
          appointmentKeys.includes('FLOOD')) &&
        INSURANCE_BUSINESS_TYPES.LIFE !== type &&
        INSURANCE_BUSINESS_TYPES.UMBRELLA !== type;
      if (!isEmpty(pageTracking) && !isEmpty(type) && pageTracking[type?.toLowerCase()]) {
        let redirectTo: any = pageTracking[type?.toLowerCase()];
        let getStep = !isEmpty(redirectTo) && PAGE_TRACKING_KEYS[redirectTo];
        if (
          INSURANCE_BUSINESS_TYPES.HOME === type ||
          INSURANCE_BUSINESS_TYPES.QUAKE === type ||
          INSURANCE_BUSINESS_TYPES.HOMEWARRANTY === type ||
          ((INSURANCE_BUSINESS_TYPES.CYBERSECURITY === type ||
            INSURANCE_BUSINESS_TYPES.LIFE === type ||
            type === INSURANCE_BUSINESS_TYPES.UMBRELLA) &&
            isEmpty(home?.personal_details?.date_of_birth)) ||
          (getStep === INSURANCE_BUSINESS_TYPES.FLOOD && (isEmpty(home?.productType) || isEmptyFloodHerdId)) ||
          (isApplicantDataRequired() && checkLobExist)
        ) {
          targetTab = 0;
        } else {
          targetTab = !isEmpty(getStep) ? lobTab[getStep] : lobTab[type];
        }
        if (getStep === 'DRIVER') {
          let { missingSpouse, driverDetails } = checkSpouseMissing(driversList);
          if (missingSpouse && driverDetails) {
            dispatch(driverSelectedForEdit(driverDetails?.duid));
            dispatch(setMissingSpouse(true));
          }
        }
        targetTab =
          INSURANCE_BUSINESS_TYPES.CYBERSECURITY === type &&
          !isEmpty(home?.personal_details?.date_of_birth)
            ? tabsCount
            : targetTab;
      } else {
        targetTab = lobTab[type];
        if (
          INSURANCE_BUSINESS_TYPES.HOME === type ||
          INSURANCE_BUSINESS_TYPES.QUAKE === type ||
          INSURANCE_BUSINESS_TYPES.HOMEWARRANTY === type ||
          ((INSURANCE_BUSINESS_TYPES.CYBERSECURITY === type ||
            INSURANCE_BUSINESS_TYPES.LIFE === type ||
            INSURANCE_BUSINESS_TYPES.UMBRELLA === type) &&
            isEmpty(home?.personal_details?.date_of_birth)) ||
          (INSURANCE_BUSINESS_TYPES.FLOOD === type &&
            (isEmpty(home?.productType) || isEmptyFloodHerdId)) ||
          (isApplicantDataRequired() && checkLobExist)
        ) {
          targetTab = 0;
        }
        targetTab =
          (INSURANCE_BUSINESS_TYPES.CYBERSECURITY === type &&
            !isEmpty(home?.personal_details?.date_of_birth)) ||
          (isHomeLobWithHerdId && !isEmpty(home?.productType))
            ? tabsCount
            : targetTab;
      }
    }
    setValue(targetTab);
    setQuoteType(type);
  };

  const disableSteps = () => {
    let isAutoPolicyBounded = !isEmpty(policyBounded) && policyBounded[LineOfBusiness.AUTO];
    return {
      flood:
        !floodDetails.isFloodInfoRequired ||
        (!isEmpty(policyBounded) && policyBounded[LineOfBusiness.FLOOD]),
      vehicle: !vehiclesList?.length || isAutoPolicyBounded,
      drvier: !driversList?.length || isAutoPolicyBounded,
      policy: isEmpty(insurance?.residency_status) || isAutoPolicyBounded,
      pet: petDetails?.isBreedError || !petDetails?.petList?.length || petDetails?.isPolicyBounded,
      life: isEmpty(home?.personal_details?.date_of_birth) || isEmpty(lifeDetails?.lifeInfo),
      umbrella:
        isEmpty(home?.personal_details?.date_of_birth) ||
        isEmpty(umbrellaDetails?.umbrellaInfo?.questionGroups),
      propertyInfo: isDisabledPropertyInfo,
    };
  };

  const tabs: { [key: string]: any } = tabElements({
    handleBack,
    handleNext,
    value,
    floodDetails: floodDetails,
    goToLastTab,
    add,
    disabledSteps: disableSteps(),
    tabsCount,
    redirectToStep,
  });
  const drawTabPanels = () => {
    return tabKey
      .filter(e => !excludeLOBFromStepper.includes(e))
      .map((lobType: any, index: number) => {
        let tabIndex = getStepperIndex(lobType, tabKey);
        return tabs[lobType].tabPanel(index + tabIndex);
      });
  };

  const drawTabs = () => {
    return tabKey
      .filter(e => !excludeLOBFromStepper.includes(e))
      .map((lobType: any, index: number) => {
        let tabIndex = getStepperIndex(lobType, tabKey);
        return tabs[lobType].tab(index + tabIndex);
      });
  };

  const handleStepperChange = (event: any, newValue: any) => {
    if (newValue !== value) {
      if (
        (lobTab['DRIVER'] === value || lobTab[INSURANCE_BUSINESS_TYPES.AUTO] === value) &&
        ((newValue === value + 1 && !enableRatePage) || newValue === tabsCount)
      ) {
        const switchData = {
          isVehicleToDriver:
            value + 1 === newValue && lobTab[INSURANCE_BUSINESS_TYPES.AUTO] === value,
          isDriverToPolicy: value + 1 === newValue && lobTab['DRIVER'] === value,
          isRedirectToRatePage: newValue === tabsCount,
        };
        dispatch(setSwitchDetailFromDriver(switchData));
      } else {
        setValue(newValue);
        dispatch(setSwitchDetailFromDriver({}));
      }
      if (lobTab['AUTO'] !== value) {
        dispatch(
          setVehicleDetails({
            isVinCleared: false,
          } as any)
        );
      }
      dispatch(setLobPriority(''));
      setAdd(false);
    }
  };

  const handleDashboardRedirect = () => {
    goToDashboard();
    dispatch(flushAppDetails());
  };
  return (
    <Suspense fallback=''>
      <div
        className={
          tabsCount === value
            ? `container quote-container rates-container  ${classes.root}`
            : `container quote-container ${classes.root}`
        }
      >
        {!inIframe() && tabKey.length ? (
          <button onClick={() => handleDashboardRedirect()} className='link-label dashboard-btn'>
            <KeyboardBackspaceIcon className='mr-2 left-arrow-icon' />
            Dashboard
          </button>
        ) : (
          ''
        )}
        {!!enableFeedbackButton && <FeedbackButton />}
        <div className='steps-wrap'>
          <AppBar
            elevation={0}
            position='static'
            className={`bg-white ${
              getStepperTabLength(appointmentList) > 8 ? 'step-header-8' : 'step-header'
            }`}
          >
            <Tabs
              variant='scrollable'
              scrollButtons='auto'
              TabIndicatorProps={{
                style: {
                  display: 'none',
                },
              }}
              value={value}
              onChange={handleStepperChange}
              aria-label='Questionnaires'
            >
              {tabKey.length && (
                <Tab
                  value={0}
                  label={APPLICANT_LABEL_NAME}
                  wrapped
                  {...allProps(0)}
                  icon={
                    <CustomIcon
                      label={APPLICANT_LABEL_NAME}
                      icon='User'
                      index={0}
                      value={value}
                      enableStep={false}
                    />
                  }
                />
              )}
              {tabKey.length && isShowPropertyInfo && (
                <Tab
                  value={tabsCount + 1}
                  label={PROPERTY_INFO.LABEL}
                  disabled={disableSteps().propertyInfo}
                  wrapped
                  {...allProps(0)}
                  icon={
                    <CustomIcon
                      label={PROPERTY_INFO.LABEL}
                      icon='Property'
                      index={tabsCount + 1}
                      value={value}
                      enableStep={false}
                    />
                  }
                />
              )}
              {drawTabs()}
              {tabKey.length && (
                <Tab
                  value={tabsCount}
                  disabled={!enableRatePage}
                  label={RATES_LABEL_NAME}
                  wrapped
                  {...allProps(0)}
                  icon={
                    <CustomIcon
                      label={RATES_LABEL_NAME}
                      icon='Rates'
                      index={tabsCount}
                      value={value}
                      enableStep={!enableRatePage}
                    />
                  }
                />
              )}
            </Tabs>
          </AppBar>
          {tabKey.length ? (
            <TabPanel label={APPLICANT_LABEL_NAME} value={value} index={0}>
              <ApplicantForm
                onNextTab={(index: number) => handleNext(index)}
                currentIndex={value}
                goToLastTab={(currValue: number) => goToLastTab(currValue)}
                redirectToStep={redirectToStep}
              />
            </TabPanel>
          ) : null}
          {tabKey.length ? (
            <TabPanel label={PROPERTY_INFO.LABEL} value={value} index={tabsCount + 1}>
              <PropertyInfo
                onNextTab={(index: number) => handleNext(index)}
                onPrevTab={(index: number) => handleBack(index)}
                currentIndex={tabsCount + 1}
                goToLastTab={() => goToLastTab(tabsCount - 1)}
                tabsCount={tabsCount}
                quoteType={quoteType}
                redirectToStep={redirectToStep}
              />
            </TabPanel>
          ) : null}
          {drawTabPanels()}
          {tabKey.length ? (
            <TabPanel label={RATES_LABEL_NAME} value={value} index={tabsCount}>
              <ProductRatesDetails
                appointments={appointments}
                onPrevTab={(index: number) => goToFirstTab(index)}
                goBackToCertainTab={(targetTab: number, type: string) =>
                  goBackToCertainTab(targetTab, type)
                }
                veriskIsLoading={veriskLoading}
                goToLastTab={(shouldGoToLastTab: boolean) => setGoToRates(shouldGoToLastTab)}
                tabsCount={tabsCount}
                setTab={setTab}
                tabValue={value}
                quoteType={quoteType}
                redirectToStep={redirectToStep}
              />
            </TabPanel>
          ) : null}
        </div>
      </div>
    </Suspense>
  );
};

export default QuoteApplication;
